import { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';

import useAuth from 'hooks/auth/use-auth';
import useErrorModal from 'hooks/use-error-modal';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';

export function useFavoriteStores() {
  const { user } = useAuth();
  const favStoresEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAVORITE_STORES);
  const showFavoritesByDefaultFlag = useFlag(
    LaunchDarklyFlag.ENABLE_FAVORITE_STORES_DEFAULT_VIEW_IF_USER_IS_LOADED
  );
  const existingFavoriteStores = useMemo(() => {
    const userFavoriteStores = user?.details?.favoriteStores || [];
    const trimmedFavoriteStores = userFavoriteStores.map(store => ({
      storeId: store.storeId,
      storeNumber: store.storeNumber || '',
    }));
    // We need to remove falsy ids that were previously saved from bad sanity data
    return trimmedFavoriteStores.filter(store => !!store.storeId);
  }, [user]);

  const showFavoritesByDefault = showFavoritesByDefaultFlag && !!existingFavoriteStores.length;
  return {
    favStoresEnabled,
    validFavStores: existingFavoriteStores,
    showFavoritesByDefault,
  };
}
export const useFavoriteStore = (storeId: string, storeNumber: string) => {
  const { formatMessage } = useIntl();
  const { updateUserFavStores, isAuthenticated } = useAuth();
  const { favStoresEnabled, validFavStores } = useFavoriteStores();
  const { updateUserAttributes } = useMParticleContext();

  const [favStoreLoading, setFavStoreLoading] = useState(false);

  const [ErrorFavoriteStoreDialog, openErrorDialog] = useErrorModal({
    onConfirm: () => null,
    modalAppearanceEventMessage: 'Error: Sign In to Set Favorite Stores',
  });

  const isFavorite = useMemo(() => !!validFavStores.find(store => store.storeId === storeId), [
    storeId,
    validFavStores,
  ]);

  const toggleFavorite = async () => {
    if (!isAuthenticated()) {
      openErrorDialog({
        title: formatMessage({ id: 'errorDialogFavoriteStoreTitle' }),
        message: formatMessage({ id: 'errorDialogFavoriteStoreMessage' }),
      });
      return;
    }

    const allFavoriteStores = isFavorite
      ? validFavStores.filter(store => store.storeId !== storeId)
      : [{ storeId, storeNumber }, ...validFavStores];
    // We need to remove falsy ids before we save to clean up bad sanity data
    const allValidFavoriteStores = allFavoriteStores.filter(store => !!store.storeId);
    setFavStoreLoading(true);
    await updateUserFavStores(allValidFavoriteStores);
    updateUserAttributes({
      favoriteStores: JSON.stringify(allValidFavoriteStores.map(store => store?.storeNumber)),
    });
    setFavStoreLoading(false);
  };

  return {
    favStoresEnabled,
    toggleFavorite,
    isFavorite,
    ErrorFavoriteStoreDialog,
    favStoreLoading,
  };
};
